import { JSONContent } from '@tiptap/core';
import { generateJSON } from '@tiptap/html';
import { FillInTheBlanksAreaPinComponent, Size, Vector } from '../../models';
import { emptyDocument, tipTapFillInTheBlanksDataExtensions } from '../../tip-tap';
import { newSeed, shuffle, splitmix32 } from '../array-shuffle';
import { newBox, newSize, newVector } from '../geometry';
import { defaultOrder } from '../order';
import { randomId } from '../random-id';

type FillInTheBlanksAreaPinComponentProps = {
  position?: Vector;
  size?: Size;
  order?: string;
  htmlContent?: string;
  settings?: Partial<FillInTheBlanksAreaPinComponent['settings']>;
  seed?: number /* 32-bit integer */;
};

export function newFillInTheBlanksAreaPinComponent({
  position = newVector(32, 124),
  size = newSize(896, 384),
  settings,
  order = defaultOrder,
  htmlContent,
  seed = newSeed(),
}: FillInTheBlanksAreaPinComponentProps): FillInTheBlanksAreaPinComponent {
  return {
    type: 'FILL_IN_THE_BLANKS_AREA',
    id: randomId(),
    settings: {
      tipTapContent: htmlContent ? generateJSON(htmlContent, tipTapFillInTheBlanksDataExtensions) : emptyDocument(),
      distractors: settings?.distractors || [],
      sortingSeed: settings?.sortingSeed || seed,
      backgroundColor: settings?.backgroundColor || '#FFFFFF',
      fontColor: settings?.fontColor || '#001343',
      fontSize: settings?.fontSize || '16px',
      fontFamily: settings?.fontFamily || 'Dm Sans',
      lineHeight: settings?.lineHeight || 1.75,
      ...settings,
      lockLayout: true,
      movableInInteractiveViewer: false,
    },
    layout: newBox(position, size),
    order,
  };
}

export function wrapStringsInParagraphs(strings: string[]): JSONContent[] {
  return strings.map((string) => ({ type: 'paragraph', content: [{ type: 'text', text: string }] }));
}

/**
 * This function will take the words and distractors and return a deterministic shuffled array of all the words
 */
export function seededWordbankOrder(words: JSONContent[], distractors: string[], sortingSeed: number): JSONContent[] {
  const allWords: JSONContent[] = [...words, ...wrapStringsInParagraphs(distractors)];
  return shuffle(allWords, splitmix32(sortingSeed ?? 123456));
}
